/**
 * Tool-call parsing.
 *
 * There is no GBNF in the browser. transformers.js has no grammar sampler, so
 * nothing can make a malformed call impossible the way constrained decoding
 * does on llama.cpp. The format IS the reliability layer here, which is why
 * this module is small, strict about what it emits, and liberal about what it
 * accepts.
 *
 * TWO DIALECTS, both implemented, because which one wins is an open question
 * and should be settled by the M3 bench rather than by preference:
 *
 *   records  search: how does the GB10 handle unified memory
 *   tag      <search>how does the GB10 handle unified memory</search>
 *
 * The case for records is our own format research: records beat JSON at 3B
 * (recall 1.0 against 0.556) and full-word keys beat terse ones badly (.97
 * against .11, where the model filled one-letter keys by continuing the
 * alphabet). The case for tags is that instruct models are fine-tuned on
 * tag-shaped tool calls, so the shape is already in the weights. Those two
 * arguments point opposite ways and only a measurement settles it.
 */

import { prompt as renderPrompt, type PromptOverrides } from "./promptKit.ts";

export const TOOL_NAMES = ["search", "imagine"] as const;
export type ToolName = (typeof TOOL_NAMES)[number];

export type Dialect = "records" | "tag";

/**
 * The sentinel for an empty result.
 *
 * Bare, NOT parenthesized. Era 2 measured a real parenthesis penalty on the
 * identical token (.252 against .141), and "(none)" scored near-worst on
 * every lane. So we emit the bare word and accept the rest.
 *
 * D3 also requires that the parser accept the sentinel its own contract
 * promises. A parser once rejected "(none)" while the prompt asked for it and
 * produced a universal 0/28 that looked like a model failure and was ours.
 * There is a unit test for exactly this.
 */
export const EMPTY_SENTINEL = "none";

const ACCEPTED_EMPTY = new Set(["none", "(none)", "n/a", "na", "null", "nil", "no_record", "norecord", "-"]);

export function isEmptyResult(s: string): boolean {
  return ACCEPTED_EMPTY.has(s.trim().toLowerCase());
}

export type ToolCall = {
  tool: ToolName;
  arg: string;
  /** The exact substring matched, for logging and for trimming the turn. */
  raw: string;
  /** Index of the first character of the call within the input. */
  start: number;
  /** Index just past the call. */
  end: number;
  dialect: Dialect;
};

const isToolName = (s: string): s is ToolName => (TOOL_NAMES as readonly string[]).includes(s);

/**
 * Anchored at a line start, tolerating indentation, list markers and bold.
 *
 * The terminator is a REAL newline, not `$` under the m flag. `$` also matches
 * end-of-string, which meant a call still being streamed parsed as complete:
 * "search: how does the GB10" would have fired while the model was still
 * writing "...handle unified memory", searching a truncated query. Caught by
 * the streaming test, which is why that test exists.
 *
 * Once generation has actually ended there is no more text coming, so the
 * caller passes allowUnterminated and end-of-string becomes a valid close.
 */
const RECORDS_RE = new RegExp(
  String.raw`^[ \t]*(?:[-*>]\s*)?\**(${TOOL_NAMES.join("|")})\**[ \t]*:[ \t]*([^\n]+?)[ \t]*\r?\n`,
  "im",
);

const RECORDS_RE_EOS = new RegExp(
  String.raw`^[ \t]*(?:[-*>]\s*)?\**(${TOOL_NAMES.join("|")})\**[ \t]*:[ \t]*([^\n]+?)[ \t]*(?:\r?\n|$)`,
  "im",
);

const TAG_RE = new RegExp(String.raw`<(${TOOL_NAMES.join("|")})>([\s\S]*?)<\/\1>`, "i");

export type ParseOpts = {
  /**
   * Accept a records call that ends at end-of-string. Only safe once the
   * model has stopped generating. During streaming this must stay false.
   */
  allowUnterminated?: boolean;
};

/**
 * Find the first complete tool call. Returns null when there is none, which
 * includes the case of a call that has begun but not finished streaming.
 */
export function parseToolCall(
  text: string,
  dialect: Dialect | "any" = "any",
  opts: ParseOpts = {},
): ToolCall | null {
  if (dialect === "records" || dialect === "any") {
    const re = opts.allowUnterminated ? RECORDS_RE_EOS : RECORDS_RE;
    const m = re.exec(text);
    if (m && m.index !== undefined) {
      const tool = m[1].toLowerCase();
      const arg = stripArg(m[2]);
      if (isToolName(tool) && arg) {
        return { tool, arg, raw: m[0], start: m.index, end: m.index + m[0].length, dialect: "records" };
      }
    }
  }
  if (dialect === "tag" || dialect === "any") {
    const m = TAG_RE.exec(text);
    if (m && m.index !== undefined) {
      const tool = m[1].toLowerCase();
      const arg = stripArg(m[2]);
      if (isToolName(tool) && arg) {
        return { tool, arg, raw: m[0], start: m.index, end: m.index + m[0].length, dialect: "tag" };
      }
    }
  }
  return null;
}

function stripArg(s: string): string {
  let out = s.trim();
  // Models like to wrap arguments in quotes or backticks. Strip one matched pair.
  const pairs: [string, string][] = [
    ['"', '"'],
    ["'", "'"],
    ["`", "`"],
  ];
  for (const [a, b] of pairs) {
    if (out.length >= 2 && out.startsWith(a) && out.endsWith(b)) {
      out = out.slice(1, -1).trim();
      break;
    }
  }
  // Trailing punctuation a model adds out of prose habit.
  out = out.replace(/[.,;]+$/, "").trim();
  return out;
}

/**
 * True once a call has started but may not be complete. Used to decide
 * whether to keep waiting rather than treating the text as a final answer.
 */
export function hasPartialToolCall(text: string, dialect: Dialect | "any" = "any"): boolean {
  if (parseToolCall(text, dialect)) return false;
  // Deliberately does NOT pass allowUnterminated: an unterminated records line
  // is exactly the in-flight case this function reports on.
  const open = new RegExp(String.raw`<(${TOOL_NAMES.join("|")})>`, "i");
  if ((dialect === "tag" || dialect === "any") && open.test(text)) return true;
  // A records call is complete only at a newline, so a trailing "search: ..."
  // with no newline yet is still in flight.
  const trailing = new RegExp(String.raw`(?:^|\n)[ \t]*(?:[-*>]\s*)?\**(${TOOL_NAMES.join("|")})\**\s*:[^\n]*$`, "i");
  return (dialect === "records" || dialect === "any") && trailing.test(text);
}

/** Stop strings for the tag dialect: a fixed close tag halts the sampler exactly. */
export function tagStopStrings(): string[] {
  return TOOL_NAMES.map((t) => `</${t}>`);
}

/** Line prefixes for the records dialect: stop at the newline that ends the call. */
export function recordsStopPrefixes(): string[] {
  return TOOL_NAMES.map((t) => `${t}:`);
}

/* ---- results back into the conversation ---- */

export type SearchResult = { fact: string; quote?: string; ref?: string; score?: number };

/**
 * Normalise for the DUPLICATE check only. Never used for display.
 *
 * The exporter frequently stores the quote as the fact verbatim, sometimes with
 * a markdown bullet still attached, so a raw equality test misses half of them.
 */
const sameText = (a: string, b: string) => {
  const n = (s: string) =>
    s
      .replace(/^\s*[-*•]\s*/, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  return n(a) === n(b);
};

export type RecordFormat = {
  /** The word before each record. See the `recordLabel` prompt for why it matters. */
  label?: string;
  /** Include the source URL. Off by default: see below. */
  includeRef?: boolean;
};

/**
 * Render results as records with FULL-WORD keys.
 *
 * The provenance framing is deliberate and load-bearing. Restraint is
 * architecture, not capability: a 27B leaked as readily as a 2B in our runs,
 * so the banner, the attribution, and the explicit instruction not to go
 * beyond the records are what hold the line, not the model's size.
 *
 * TWO THINGS THIS USED TO WASTE, both spotted by Free reading a real transcript
 * in the lab on the day the lab existed, which is the whole argument for
 * building it.
 *
 * 1. EVERY FACT WAS PRINTED TWICE. The exporter stores the quote as the fact
 *    verbatim for most rows, and this emitted `fact:` and then `quote:` with
 *    identical content. Six records became twelve copies of six sentences. That
 *    is not merely wasteful: showing a small model the same sentence twice,
 *    immediately, is an invitation to reproduce it verbatim, and verbatim
 *    reproduction is the failure this bay has spent the most effort on.
 *    A quote is now emitted ONLY when it genuinely differs from the fact.
 *
 * 2. THE SOURCE URL WAS IN THE MODEL'S CONTEXT. The system prompt forbids the
 *    model to mention sources at all, so every one of those URLs was tokens
 *    spent on something it was never allowed to use. Refs belong in the UI,
 *    next to the fact, where a reader can follow them. Off by default here.
 */
export function formatSearchResults(results: SearchResult[], fmt: RecordFormat = {}): string {
  const label = fmt.label ?? renderPrompt("recordLabel");
  if (results.length === 0) return `result: ${EMPTY_SENTINEL}`;
  return results
    .map((r) => {
      const lines = [`${label}: ${r.fact}`];
      // Only when it adds something. See note 1 above.
      if (r.quote && !sameText(r.quote, r.fact)) lines.push(`quote: ${r.quote}`);
      if (fmt.includeRef && r.ref) lines.push(`ref: ${r.ref}`);
      return lines.join("\n");
    })
    .join("\n\n");
}

/**
 * Wrap retrieved facts as MEMORY, not as documents.
 *
 * The first version of this said: "The following records come from the memory
 * of X. They are the only source you may use for factual claims. Do not add
 * facts that are not present below. ... End of records."
 *
 * Four uses of "records", a legalistic "only source you may use", and a
 * closing "End of records". Free's verdict was exact: "it's basically just
 * quoting the lattice facts", and every answer opened "Based on the records
 * provided, here is..." followed by a bulleted transcription.
 *
 * The model was obeying perfectly. Told three times over that it was working
 * from supplied documents, it behaved like a citation service.
 *
 * The constraint has NOT been relaxed. Era 2 is clear that restraint is
 * architecture rather than capability, and a public page must not invent facts
 * about someone's work. What changed is the ONTOLOGY: the same limit is now
 * expressed as the edge of what this mind remembers, rather than as compliance
 * with an attached file. "Do not invent what you do not remember" binds just
 * as tightly as "do not add facts not present below", and it produces someone
 * speaking instead of something quoting.
 */
/**
 * Which wording to use.
 *
 * `auto` gates on recall strength and is what ships. The other two exist so the
 * two wordings can be compared on the SAME questions, because the open question
 * is whether the hedged one costs grounding. Era 2 holds that what moves a small
 * model is how hard the text binds it to the facts, so "the hedge is safe" is a
 * claim that has to be measured rather than assumed.
 */
export type BannerMode = "auto" | "confident" | "hedged" | "deliberate";

export function memoryBanner(
  results: SearchResult[],
  mode: BannerMode = "auto",
  ov?: PromptOverrides,
): string {
  const body = formatSearchResults(results);
  // SHORT on purpose. The previous version was ~200 words of behavioural
  // instruction sitting immediately before generation, competing with a ~90
  // word persona prompt. A 0.8B model given that much simultaneous direction
  // degrades: the observed answer restated the question, invented a
  // "container-shorthand" property, and then repeated two whole paragraphs
  // verbatim.
  //
  // The voice rules now live in the system prompt, stated once. What sits here
  // is the knowledge plus the single rule that has to win at the point of
  // generation: everything specific must come from above.
  // Arrived at after three swings, each of which taught something:
  //
  //   v1  "records ... the only source you may use"  -> well grounded, but
  //       every answer opened "Based on the records provided" and transcribed
  //       the facts as bullets. Free: "just quoting the lattice facts".
  //   v2  long memory-framed banner, ~200 words     -> better voice, but it
  //       invented a "container-shorthand" property and repeated paragraphs.
  //   v3  terse banner, rules moved to the system   -> natural, but it ignored
  //       the retrieved facts entirely and answered flexbox from its prior.
  //
  // The variable that actually moves at 0.8B is how hard the text BINDS the
  // model to the retrieved facts. Loosen it and grounding goes; tighten it and
  // the citation voice comes back. You cannot have both from wording alone at
  // this size, so this keeps v1's binding strength and kills only the two
  // surface habits that made it read like a machine: the "Based on..." opener
  // and the reflexive bullet list.
  //
  // Voice genuinely improves with model size. The 2B tier follows the "in your
  // own words" instruction while still respecting the binding, which is worth
  // knowing when choosing a default.
  //
  // THE BINDING IS NOW CONDITIONAL, and that is a bug fix rather than a
  // relaxation. See `CONFIDENT_RECALL` and the weak branch below.
  /**
   * A THINK pass is not an answer, and the ordinary banner tells it that it is.
   *
   * `think()` runs through `converse`, so every deliberation pass has been
   * receiving the full answer banner, which closes with "Put it in your own
   * words as flowing prose" and "Start with the answer" while the think prompt
   * immediately after it says "Do not answer yet". Two instructions, opposite
   * demands, and the banner is the one carrying six concrete facts.
   *
   * Measured consequence, 2026-07-27: asked to note where the opponent was
   * wrong, the Gardener produced a topic essay about volatile organic compounds
   * and never mentioned the opponent at all. The note is meant to be the thing
   * the agent brings of its OWN to the rebuttal, and instead it came back as a
   * second answer to the question.
   *
   * THE BINDING SENTENCE IS VERBATIM, deliberately. Today's A/B showed that
   * rewording it costs 37 points on a 270M model, so this variant changes only
   * what the output should LOOK like, never how hard it binds.
   */
  if (mode === "deliberate") {
    return renderPrompt("bannerDeliberate", { facts: body }, ov);
  }

  // MEASURED 2026-07-27: `auto` NEVER HEDGES, and that is a retreat with a
  // table behind it.
  //
  // The hedged wording was built to fix a real bug and then A/B'd against the
  // confident one on the same questions. All three confident arms reproduced
  // their recorded baselines exactly, so the wording was the only variable:
  //
  //     model            confident        hedged
  //                    grounded  fab    grounded  fab
  //     gemma-3-1b         90%     1        100%    0
  //     gemma-3-270m       44%    10          7%   14
  //     bonsai-t1.7b       82%     2         45%    6
  //     qwen3.5-0.8b       38%     5         22%   14
  //
  // Everything below the 1B tier fell apart. Qwen asserted MORE when hedged,
  // 8 claims to 18, and invented 14 of the 18. The lone gain is one claim out
  // of ten on the one model where the binding matters least.
  //
  // This is Era 2's finding arriving again from a new direction: what moves a
  // small model is how hard the text BINDS it to the retrieved facts. Loosen
  // the words and grounding goes, and the weaker the model the more it goes.
  //
  // So the hedged branch stays reachable for experiments and is NOT reachable
  // by default. It has a large measured cost and, as of now, no measured
  // benefit anywhere: the case that motivated it does not even trigger the gate
  // (those facts score 0.605 against a 0.5 threshold, see `recallIsWeak`).
  //
  // The likely right answer for weak recall is WITHHOLD, not hedge: show no
  // facts at all and let the existing empty-sentinel path say "I have no record
  // of it". That keeps the hard binding everywhere it is used instead of
  // softening it exactly where the model has least to stand on. Unmeasured, so
  // not shipped. See task #46.
  if (mode === "hedged") {
    return renderPrompt("bannerHedged", { facts: body }, ov);
  }
  return renderPrompt("bannerConfident", { facts: body }, ov);
}

/**
 * Above this, recall is treated as confident enough to bind the answer to.
 *
 * The aperture's `RELEVANCE_FLOOR` (0.45) decides whether a fact is offered at
 * all. This decides whether what survived is trustworthy enough to be called
 * THE answer material. The gap between them is real and it is where the failure
 * measured on 2026-07-27 lived.
 *
 * Same number as the agenda's weak-lattice warning, deliberately: "its memory
 * has little to say on this" and "do not bind the answer to this" are one
 * judgement, and two constants would drift apart. `debateAgenda` imports it.
 *
 * Inherited as a guess from `RELEVANCE_FLOOR`, which says so itself. It wants a
 * precision/recall sweep, and until it gets one this comment is the honest
 * label rather than a claim of calibration.
 */
export const CONFIDENT_RECALL = 0.5;

/**
 * Is this recall too weak to assert as the only permitted source?
 *
 * WHY THIS EXISTS. The banner used to end with "Answer using only what is
 * above" unconditionally, whatever came back. Measured on 2026-07-27, in a
 * debate about whether plants signal each other chemically, the Hermeticist's
 * retrieval returned six rows about the Emerald Tablet, the Golden Dawn and
 * Corpus Hermeticum verse 11. Nothing about plants, and then: answer using only
 * that.
 *
 * The instruction cannot be obeyed. So the model discarded the banner, and
 * discarded the restraint rule along with it, and wrote from the shape of the
 * task alone. **An impossible instruction is worse than no instruction**,
 * because it teaches the model that this whole block can be ignored.
 *
 * Scores absent means an older caller or a synthetic result, and those keep the
 * previous behaviour: bind hard. A missing score is not evidence of weakness.
 */
export function recallIsWeak(results: SearchResult[]): boolean {
  const scored = results.map((r) => r.score).filter((s): s is number => typeof s === "number");
  if (scored.length === 0) return false;
  return Math.max(...scored) < CONFIDENT_RECALL;
}

/**
 * The phrases that mark the failure mode, for the bench to score against.
 * If any of these survive in an answer, the framing has slipped back.
 */
export const META_NARRATION = [
  "based on the records",
  "based on the provided",
  "based on the information provided",
  "according to the records",
  "the records provided",
  "the records indicate",
  "the records show",
  "from the records",
  "according to my memory",
  "the provided context",
  "the given information",
  "as per the records",
];

export function hasMetaNarration(text: string): string | null {
  const t = text.toLowerCase();
  return META_NARRATION.find((p) => t.includes(p)) ?? null;
}
